import React, { useState } from "react";
import { Button, CardList } from "@panda0/panda0-ui";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";

// Quiz Results Component quiz-results-section.tsx
const QuestionAnalysis = ({ questionAnalysisRef, questions }) => {
  const [activeQuestion, setActiveQuestion] = useState(0);

  return (
    <div
      id="quiz-results"
      ref={questionAnalysisRef}
      className="pb-10 pt-6 border-t"
    >
      <div className="flex items-center mb-6">
        <div className="h-8 w-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mr-3">
          <FileText className="h-4 w-4" />
        </div>
        <h2 className="text-xl font-bold">Question Analysis</h2>
      </div>

      <CardList.Card outerClassName="border rounded-lg shadow-sm p-4">
        {/* Question Navigator */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-600 mb-3">Questions</h4>
          <div className="flex overflow-x-auto pb-2 gap-2">
            {questions.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveQuestion(idx)}
                className={`flex items-center justify-center min-w-10 h-10 rounded-md border text-sm font-medium ${activeQuestion === idx
                    ? "bg-blue-100 border-blue-300 text-blue-800"
                    : "bg-white border-gray-300 hover:bg-gray-50"
                  }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Active Question */}
        <div className="border rounded-lg overflow-hidden mb-6">
          {/* Question Header */}
          <div className="bg-gray-50 p-4 border-b flex items-center justify-between">
            <div>
              <span className="inline-flex items-center justify-center bg-blue-100 text-blue-800 rounded-md text-sm font-medium px-2.5 py-0.5 mr-2">
                Question {activeQuestion + 1}
              </span>
              <span className="inline-flex items-center justify-center bg-purple-100 text-purple-800 rounded-md text-sm font-medium px-2.5 py-0.5">
                {questions[activeQuestion].bloomsTaxonomyTags[0]}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              Experience Level: {questions[activeQuestion].experience_level}+
              years
            </div>
          </div>

          {/* Question Content */}
          <div className="p-6">
            <p className="text-lg font-medium mb-6">
              {questions[activeQuestion].question}
            </p>

            <div className="space-y-3">
              {questions[activeQuestion].options.map((option, optIdx) => {
                // Determine if this is the correct answer (highest score)
                const isCorrect =
                  Math.max(
                    ...questions[activeQuestion].options.map((o) => o.score)
                  ) === option.score;

                // Randomly select an answer the user chose (for demo purposes)
                const userSelected = optIdx === 1; // Assume user selected the second option

                let optionClass =
                  "border border-gray-200 hover:border-gray-300 bg-white";
                let checkmarkClass = "hidden";

                if (userSelected && isCorrect) {
                  optionClass = "border-green-300 bg-green-50";
                  checkmarkClass = "text-green-500";
                } else if (userSelected && !isCorrect) {
                  optionClass = "border-red-300 bg-red-50";
                  checkmarkClass = "text-red-500";
                } else if (isCorrect) {
                  optionClass = "border-green-300 bg-white";
                }

                return (
                  <div
                    key={optIdx}
                    className={`rounded-lg ${optionClass} p-4 relative cursor-pointer transition-all`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center rounded-full bg-gray-100 h-6 w-6 text-sm font-medium mt-0.5">
                        {String.fromCharCode(65 + optIdx)}
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-800">{option.option}</p>

                        {/* Only show explanation for selected or correct options */}
                        {/* {(userSelected || isCorrect) && (
                          <div className="mt-2 text-sm bg-gray-50 p-3 rounded-md">
                            {isCorrect ? (
                              <p className="text-green-700">
                                This is the correct answer. It demonstrates the
                                best practice for structuring an application.
                              </p>
                            ) : (
                              <p className="text-red-700">
                                This approach would work but isn't optimal for
                                long-term maintainability.
                              </p>
                            )}
                          </div>
                        )} */}

                        {/* Skill tags */}
                        {option.skill_tags && option.skill_tags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {option.skill_tags.map((tag, tagIdx) => (
                              <span
                                key={tagIdx}
                                className="inline-flex items-center justify-center bg-blue-50 text-blue-700 rounded-md text-xs px-2 py-0.5"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Checkmark or X for user selection */}
                      {userSelected && (
                        <div
                          className={`absolute right-4 top-4 ${checkmarkClass}`}
                        >
                          {isCorrect ? (
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 13l4 4L19 7"
                              ></path>
                            </svg>
                          ) : (
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                              ></path>
                            </svg>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="border-t p-4 flex justify-between">
            <Button
              variant="outline"
              text="Previous"
              icon={<ChevronLeft className="h-4 w-4 mr-1" />}
              disabled={activeQuestion === 0}
              onClick={() => setActiveQuestion((prev) => Math.max(0, prev - 1))}
            />
            <Button
              variant="outline"
              text="Next"
              iconPosition="right"
              icon={<ChevronRight className="h-4 w-4 ml-1" />}
              disabled={activeQuestion === questions.length - 1}
              onClick={() =>
                setActiveQuestion((prev) =>
                  Math.min(questions.length - 1, prev + 1)
                )
              }
            />
          </div>
        </div>

        {/* Score Breakdown */}
        {/* <div className="mt-8">
          <h4 className="font-medium text-base mb-3">Score Breakdown</h4>
          <div className="border rounded-lg">
            <div className="grid grid-cols-4 p-2 font-medium text-sm bg-gray-50 border-b">
              <div>Question</div>
              <div>Difficulty</div>
              <div>Score</div>
              <div>Time Taken</div>
            </div>
            <div className="divide-y">
              {questions.map((_, idx) => {
                // Generate random values for demonstration
                const score = Math.floor(Math.random() * 11);
                const max = 10;
                const timeTaken = `${Math.floor(Math.random() * 2) + 1}:${Math.floor(
                  Math.random() * 60
                )
                  .toString()
                  .padStart(2, "0")}`;
                const difficulty = ["Easy", "Medium", "Hard"][
                  Math.floor(Math.random() * 3)
                ];

                return (
                  <div key={idx} className="grid grid-cols-4 p-3 text-sm">
                    <div>Question {idx + 1}</div>
                    <div>{difficulty}</div>
                    <div className="flex items-center">
                      <span
                        className={`font-medium ${score >= 7 ? "text-green-600" : score >= 5 ? "text-yellow-600" : "text-red-600"}`}
                      >
                        {score}/{max}
                      </span>
                    </div>
                    <div>{timeTaken}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div> */}
      </CardList.Card>
    </div>
  );
};

export default React.memo(QuestionAnalysis);
